import { useMemo } from "react";
import { cn } from "../cn";

/**
 * MiniGrid — a compact SVG minimap of a listed cell set, framed to its own
 * bounding box (with a little context margin) inside the 1,000×1,000 world.
 * Presentation-only; cells are backend-authoritative.
 */
export function MiniGrid({
  cells,
  zoneClass,
  glow = true,
  testId,
}: {
  cells: [number, number][];
  zoneClass: string;
  glow?: boolean;
  testId?: string;
}) {
  const { vb, rects } = useMemo(() => {
    if (!cells || cells.length === 0) {
      return { vb: "0 0 10 10", rects: [] as [number, number][] };
    }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [x, y] of cells) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
    const w = maxX - minX + 1;
    const h = maxY - minY + 1;
    const pad = Math.max(1, Math.round(Math.max(w, h) * 0.35));
    const side = Math.max(w, h) + pad * 2;
    const ox = minX - pad - (side - w) / 2 + pad;
    const oy = minY - pad - (side - h) / 2 + pad;
    const originX = minX - (side - w) / 2;
    const originY = minY - (side - h) / 2;
    void ox; void oy;
    return {
      vb: `${originX} ${originY} ${side} ${side}`,
      rects: cells.map(([x, y]) => [x, y] as [number, number]),
    };
  }, [cells]);

  return (
    <svg
      data-testid={testId}
      viewBox={vb}
      preserveAspectRatio="xMidYMid meet"
      className="w-full h-full"
      style={glow ? { filter: "drop-shadow(0 0 3px rgba(55,224,200,0.35))" } : undefined}
      aria-hidden
    >
      <rect x="-100000" y="-100000" width="200000" height="200000" className="fill-elevated/40" />
      {rects.map(([x, y], i) => (
        <rect key={i} x={x} y={y} width={1} height={1} className={cn(zoneClass)} rx={0.08} />
      ))}
    </svg>
  );
}
